import React, { FC } from 'react';
import clsx from 'clsx';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { AuctionSearchingRequestSortTypeEnum } from 'utils/types/AuctionModels';
import { BailiffNoticeSearchingRequestSortTypeEnum } from 'utils/types/BailiffModels';
import { CooperativeSearchingRequestSortTypeEnum } from 'utils/types/CooperativeModels';
import { SortType } from 'utils/types/SaleModels';
import { TenderSearchingRequestSortTypeEnum } from 'utils/types/TenderModels';

import BackToSearchButton from './atom/BackToSearchButton';
import AutocompleteInput from './common/Inputs/AutocompleteInput/AutocompleteInput';

const OffersHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: 20px 20px 0 20px;
    padding: 0 0 10px 24px;
    border-bottom: 1px solid #030D451A;
    flex-direction: row;

    @media (max-width: 1400px) {
        padding: 0 0 10px;
    }

    &.favorites-page {
        margin-right: 45px;
    }

    & > div {
        align-items: flex-start;
        display: flex;
        gap: 40px;

        &:last-child {
            align-self: flex-end;
        }

        @media (max-width: 768px) {
            gap: 10px;
        }

        a:not(#back-to-filters) {
            display: block;

            @media (max-width: 1400px) {
                display: block;
                position: absolute;
                left: 40px;
                top: 20px;
                white-space: nowrap;
            }
        }
    }

    @media (max-width: 1400px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
    }
`;

const InputWrapper = styled.div`
    max-width: 275px;
    position: relative;

    &:hover {
        cursor: pointer;
    }

    div {
        line-height: 20px;
        column-gap: 0;
    }

    input {
        --font-weight: 600;
        font-size: var(--font-size-body-1);
    }
`;

const CheckBoxWrapper = styled.label`
    margin-top: 20px;
    display: flex;
    align-items: center;
    gap: 10px;

    @media (max-width: 768px) {
        flex-direction: row-reverse;
        margin: 0px;
    }
`;

const Checkbox = styled.input`
    pointer-events: none;
`;

const Title = styled.span<{ isBigger?: boolean, hasOpinionContainer?: boolean }>`
    min-height: 50px;
    display: ${(props) => props.hasOpinionContainer ? 'flex' : 'block'};
    gap: 25px;
    margin: 10px 0 0 15px;
    font-weight: var(--font-weight);
    font-size: 20px;
    line-height: 30px;
    color: var(--color-primary);

    span {
        font-size: 20px;
        font-weight: 400;
        color: var(--color-primary);
    }

    @media (min-width: 1401px) {
        margin-top: 0;
        min-height: 0;
    }

    @media (max-width: 1400px) {
        min-height: ${(props) => {
            if (props.hasOpinionContainer) return '160px';
            if (props.isBigger) return '150px';

            return '50px';
        }};
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        gap: ${(props) => props.isBigger ? '20px' : '5px'};
    }
`;

const TitleText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 768px) {
        display: inline;

        span {
            margin-left: 6px;
        }
    }
`;

const ChatbotAndOpinionWrapper = styled.div`
    margin-left: 250px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 1400px) {
        margin-left: 0px;
        flex-direction: column;
        margin: 0px;
        align-items: flex-start;
        gap: 10px;
    }
`;

const BottomSection = styled.div`
    display: flex;

    @media (min-width: 1401px) {
        &&& {
            align-self: baseline;
            align-items: baseline;
        }

        && > * {
            margin-top: 0;
        }
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        margin: 16px auto auto 15px;
    }
      
`;

const BaseClickableSpan = styled.span`
    margin-top: 20px;
    margin-left: 15px;
    font-weight: var(--font-weight);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-primary);
    cursor: pointer;

    @media (max-width: 1400px) {
        margin-top: 0;

        &:first-child:not(.always-visible) {
            display: none;
        }
    }

    b {
        font-weight: 600;
    }

    &:hover {
        color: var(--color-alt);
    }
`;

const BaseClickableLink = styled.a`
    margin-top: 20px;
    font-weight: var(--font-weight);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-primary);
    cursor: pointer;
    text-decoration-line: underline;

    @media (max-width: 1400px) {
        margin-top: 0;
        display: block !important;
    }

    &:hover {
        color: var(--color-alt);
    }
`;

const ResetButton = styled.span`
    margin-top: 20px;
    font-weight: var(--font-weight);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-primary);
    cursor: pointer;
    text-decoration-line: underline;

    &:hover {
        color: var(--color-alt);
    }
`;

const enum HeaderTypes {
    FAVORITES = 'favorites',
}

interface V2HeaderSectionProperties {
    type?: HeaderTypes,
    title: string,
    offersCount?: number,
    handleCSVClick?: () => void,
    handlePDFClick?: () => void,
    showPDFOnSmallScreens?: boolean,
    sortType?: SortType | BailiffNoticeSearchingRequestSortTypeEnum | TenderSearchingRequestSortTypeEnum | AuctionSearchingRequestSortTypeEnum | CooperativeSearchingRequestSortTypeEnum,
    sortOptions?: {label: string, value: string}[],
    handleChangeSort?: (sort: string) => void,
    handleBackToFilters?: () => void,
    showLowerOriginalPriceOffers?: boolean,
    showLowerPriceCheckInput?: boolean,
    handleShowLowerOriginalPriceOffers?: (sortType: SortType) => void,
    handleShowUnblockedOffers?: (sortType: SortType) => void,
    showUnblockedOffers?: boolean,
    showUnblockedOffersInput?: boolean,
    chatbotButton?: React.ReactNode,
    opinionChatContainer?: React.ReactNode
    handleReset?: () => void,
    customStepBackText?: string,
    additionalElement?: React.ReactNode
}

const V2HeaderSection: FC<V2HeaderSectionProperties> = ({
    type,
    handlePDFClick,
    showPDFOnSmallScreens,
    title,
    offersCount,
    handleCSVClick,
    sortType,
    sortOptions,
    handleChangeSort,
    handleBackToFilters,
    showLowerOriginalPriceOffers,
    showLowerPriceCheckInput,
    handleShowLowerOriginalPriceOffers,
    handleShowUnblockedOffers,
    showUnblockedOffers,
    showUnblockedOffersInput,
    handleReset,
    chatbotButton,
    opinionChatContainer,
    customStepBackText,
    additionalElement
}) => {
    const { user } = useUser();

    return (
        <>
            <OffersHeader className={clsx({ 'favorites-page': type === HeaderTypes.FAVORITES })}>
                <Title isBigger={chatbotButton ? true : false} hasOpinionContainer={opinionChatContainer ? true : false}>
                    <TitleText>
                        {title}
                        {offersCount ? <span>({offersCount})</span> : null}
                    </TitleText>
                    <ChatbotAndOpinionWrapper>
                        {chatbotButton && chatbotButton}
                        {opinionChatContainer && opinionChatContainer}
                    </ChatbotAndOpinionWrapper>
                </Title>
                <BottomSection>
                    {offersCount && offersCount > 0 && handleCSVClick
                        ? <BaseClickableSpan className={'exel'} onClick={handleCSVClick}>
                            Pobierz plik <b>EXCEL</b>
                        </BaseClickableSpan>
                        : null}
                    {showLowerPriceCheckInput && showLowerPriceCheckInput
                        ? <CheckBoxWrapper>
                            <div>Tylko ze zmienioną ceną</div>
                            <Checkbox
                                readOnly
                                checked={showLowerOriginalPriceOffers}
                                onChange={handleShowLowerOriginalPriceOffers}
                                type="checkbox"
                                autocomplete="off"/>
                        </CheckBoxWrapper>
                        : null
                    }
                    {user.isInOrganization && handleShowUnblockedOffers && showUnblockedOffersInput
                        ? <CheckBoxWrapper>
                            <div>Odblokowane</div>
                            <Checkbox
                                readOnly
                                checked={showUnblockedOffers}
                                onChange={handleShowUnblockedOffers}
                                type="checkbox"
                                autocomplete="off"/>
                        </CheckBoxWrapper>
                        : null
                    }
                    {offersCount && sortOptions && sortOptions.length > 0 && handleChangeSort
                        ? <InputWrapper>
                            <AutocompleteInput
                                headerInput
                                oneLine
                                isInputMode={false}
                                value={sortType}
                                // @ts-expect-error TODO: INVEST-253
                                onChange={handleChangeSort}
                                placeholder="Sortuj po..."
                                prefix="Sortuj"
                                options={sortOptions}
                                alignSuggestionsLeft={true}
                            />
                        </InputWrapper>
                        : null}
                    {handlePDFClick &&
                        <BaseClickableSpan className={clsx({ 'always-visible': showPDFOnSmallScreens })} onClick={handlePDFClick}>
                            Pobierz plik <b>PDF</b>
                        </BaseClickableSpan>}
                    {handleBackToFilters
                        ? customStepBackText
                            ? <BaseClickableLink id="back-to-filters" onClick={handleBackToFilters} href={'#filters'}>
                                {customStepBackText}
                            </BaseClickableLink>
                            : <BackToSearchButton onClick={handleBackToFilters} />
                        : null}
                    {handleReset
                        ? <ResetButton onClick={handleReset} href={'#'}>Przywróć wartości domyślne</ResetButton>
                        : null}
                </BottomSection>
                {additionalElement && additionalElement}
            </OffersHeader>
        </>
    );
};

export default V2HeaderSection;
